import { useEffect, useState } from "react";
import { Modal } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  IconDefinition,
} from "@fortawesome/sharp-regular-svg-icons";
import { faArrowLeft } from "@fortawesome/sharp-light-svg-icons";
import { useMutation } from "@apollo/client";
import { MIME_TYPES } from "@mantine/dropzone";
import { ChooseDocumentTypeModalView } from "../modal-views/choose-document-type-modal-view";

import { UploadDocumentModalView } from "../modal-views/upload-document-modal-view";
import { ViewFileModalView } from "../modal-views/view-files-modal-view";
import { DeleteConfirmationModalView } from "../modal-views/delete-confirmation-modal-view";
import {
  DELETE_APPEAL_SUPPORTING_DOCUMENT,
  GENERATE_SUPPORTING_DOCUMENT_UPLOAD_URLS,
  GET_SUPPORTING_DOCUMENTS_FOR_APPEAL,
  UPSERT_APPEAL_SUPPORTING_DOCUMENT,
} from "../../graphql/requests";
import {
  DocumentUploadModalMode,
  DocumentUploadModalNavigationDirection,
} from "../../types/modal";
import { CloseConfirmationModalView } from "../modal-views/close-confirmation-modal-view";

import styles from "./document-upload-modal.module.scss";
import {
  AppealSupportingDocument,
  AppealSupportingDocumentFileType,
  AppealSupportingDocumentType,
} from "@/__generated__/graphql.ts";
import { downloadFile, uploadFileToUrl } from "@/utils/file-requests.ts";
import { getFileUrlsForFile } from "@/utils/file-conversion.ts";
import { getPageCountForFile } from "@/utils/page-count.ts";
import { getClientSafeErrorMessage } from "shared/src/utils/apollo-error-utils";

// Type for supporting documents without isIncludedInAppeal (provider users can see all documents)
type ProviderAppealSupportingDocument = Omit<
  AppealSupportingDocument,
  "isIncludedInAppeal"
>;

enum DocumentUploadModalView {
  CHOOSE_DOCUMENT_TYPE = "CHOOSE_DOCUMENT_TYPE",
  UPLOAD_DOCUMENT = "UPLOAD_DOCUMENT",
  VIEW_FILE = "VIEW_FILE",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  CLOSE_CONFIRMATION = "CLOSE_CONFIRMATION",
}

type ChooseDocumentTypeView = {
  view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE;
};

type UploadDocumentView = {
  view: DocumentUploadModalView.UPLOAD_DOCUMENT;
  documentType: AppealSupportingDocumentType;
  otherDocumentName: string | null;
};

type ViewFileView = {
  view: DocumentUploadModalView.VIEW_FILE;
  file: File;
};

type DeleteConfirmationView = {
  view: DocumentUploadModalView.DELETE_CONFIRMATION;
  file: File;
};

type CloseConfirmationView = {
  view: DocumentUploadModalView.CLOSE_CONFIRMATION;
  navigationDirection: DocumentUploadModalNavigationDirection;
};

type ModalView =
  | ChooseDocumentTypeView
  | UploadDocumentView
  | ViewFileView
  | DeleteConfirmationView
  | CloseConfirmationView;

const HeadingIcon = () => (
  <div className={styles.headingIcon}>
    <FontAwesomeIcon icon={faFileLines} />
  </div>
);

type BackToUploadViewButtonProps = {
  onClick: () => void;
};

const BackToUploadViewButton = ({ onClick }: BackToUploadViewButtonProps) => (
  <button
    onClick={onClick}
    type="button"
    aria-label="Go back to your uploaded files"
    className={styles.backToUploadViewButton}
  >
    <FontAwesomeIcon icon={faArrowLeft as IconDefinition} />
  </button>
);

type TitleProps = {
  currentView: DocumentUploadModalView;
  onNavigate: () => void;
};

const Title = ({ currentView, onNavigate }: TitleProps) => {
  if (currentView === DocumentUploadModalView.VIEW_FILE) {
    return <BackToUploadViewButton onClick={onNavigate} />;
  }

  return <HeadingIcon />;
};

type DocumentUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitDocument: () => void;
  mode: DocumentUploadModalMode;
  document: ProviderAppealSupportingDocument | null;
  onLoadingDocumentFilesError: () => void;
  appealId: string;
};

export const DocumentUploadModal = ({
  isOpen,
  onClose,
  onSubmitDocument,
  mode,
  document,
  onLoadingDocumentFilesError,
  appealId,
}: DocumentUploadModalProps) => {
  const [currentView, setCurrentView] = useState<ModalView>({
    view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
  });
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<AppealSupportingDocumentType | null>(null);
  const [otherDocumentName, setOtherDocumentName] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [saveDocumentError, setSaveDocumentError] = useState<string | null>(
    null,
  );
  const [isDocumentUpsertLoading, setIsDocumentUpsertLoading] = useState(false);
  const [viewFileUrls, setViewFileUrls] = useState<string[] | null>(null);
  const [isGettingViewFileUrls, setIsGettingViewFileUrls] = useState(false);
  const [hasErrorGettingViewFileUrls, setHasErrorGettingViewFileUrls] =
    useState(false);
  const [isLoadingDocumentFiles, setIsLoadingDocumentFiles] = useState(false);
  const [initialFiles, setInitialFiles] = useState<File[]>([]);

  const [generateUploadUrls] = useMutation(
    GENERATE_SUPPORTING_DOCUMENT_UPLOAD_URLS,
  );

  const [upsertAppealSupportingDocument] = useMutation(
    UPSERT_APPEAL_SUPPORTING_DOCUMENT,
    {
      refetchQueries: [
        {
          query: GET_SUPPORTING_DOCUMENTS_FOR_APPEAL,
          variables: {
            appealId,
          },
        },
      ],
    },
  );

  const [deleteDocument] = useMutation(DELETE_APPEAL_SUPPORTING_DOCUMENT);

  useEffect(() => {
    const handleLoadingViewFileUrls = async () => {
      if (currentView.view === DocumentUploadModalView.VIEW_FILE) {
        setIsGettingViewFileUrls(true);
        setHasErrorGettingViewFileUrls(false);

        try {
          const fileUrls = await getFileUrlsForFile(currentView.file);

          setViewFileUrls(fileUrls);
        } catch (error) {
          /* oxlint-disable-next-line no-console */
          console.error(
            `Error getting file urls to view document file ${error}`,
          );
          setHasErrorGettingViewFileUrls(true);
        } finally {
          setIsGettingViewFileUrls(false);
        }
      }
    };

    handleLoadingViewFileUrls();
  }, [currentView]);

  useEffect(() => {
    if (mode === DocumentUploadModalMode.ADD) {
      setCurrentView({
        view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
      });
    } else if (mode === DocumentUploadModalMode.ADD_LOMN) {
      setCurrentView({
        view: DocumentUploadModalView.UPLOAD_DOCUMENT,
        documentType: AppealSupportingDocumentType.LetterOfMedicalNecessity,
        otherDocumentName: null,
      });
      setSelectedDocumentType(
        AppealSupportingDocumentType.LetterOfMedicalNecessity,
      );
    } else if (mode === DocumentUploadModalMode.ADD_LAB_RESULTS) {
      setCurrentView({
        view: DocumentUploadModalView.UPLOAD_DOCUMENT,
        documentType: AppealSupportingDocumentType.LabResults,
        otherDocumentName: null,
      });
      setSelectedDocumentType(AppealSupportingDocumentType.LabResults);
    } else if (mode === DocumentUploadModalMode.ADD_DENIAL_LETTER) {
      setCurrentView({
        view: DocumentUploadModalView.UPLOAD_DOCUMENT,
        documentType: AppealSupportingDocumentType.DenialLetter,
        otherDocumentName: null,
      });
      setSelectedDocumentType(AppealSupportingDocumentType.DenialLetter);
    } else if (!document) {
      throw new Error(
        "Document was not provided when setting document upload modal to edit mode",
      );
    } else {
      const loadDocumentFiles = async () => {
        setIsLoadingDocumentFiles(true);

        try {
          const downloadedDocumentFiles = await Promise.all(
            document.files.map((documentFile) =>
              downloadFile(documentFile.url),
            ),
          );

          setUploadedFiles(downloadedDocumentFiles);
          setInitialFiles(downloadedDocumentFiles);
        } catch (error) {
          /* oxlint-disable-next-line no-console */
          console.error("Error loading document files:", error);
          onLoadingDocumentFilesError();
        } finally {
          setIsLoadingDocumentFiles(false);
        }
      };

      setSelectedDocumentType(document.documentType);
      setCurrentView({
        view: DocumentUploadModalView.UPLOAD_DOCUMENT,
        documentType: document.documentType,
        otherDocumentName: document.otherDocumentTypeName ?? null,
      });
      loadDocumentFiles();
    }
  }, [isOpen, mode, document, onLoadingDocumentFilesError]);

  const hasUnsavedChanges =
    (initialFiles.length && initialFiles !== uploadedFiles) ||
    (!initialFiles.length && uploadedFiles.length);

  const closeModal = () => {
    onClose();
    setCurrentView({ view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE });
    setSelectedDocumentType(null);
    setOtherDocumentName("");
    setUploadedFiles([]);
    setSaveDocumentError(null);
    setViewFileUrls(null);
    setIsGettingViewFileUrls(false);
    setHasErrorGettingViewFileUrls(false);
    setInitialFiles([]);
  };

  const handleOnClose = () => {
    if (hasUnsavedChanges) {
      setCurrentView({
        view: DocumentUploadModalView.CLOSE_CONFIRMATION,
        navigationDirection: DocumentUploadModalNavigationDirection.CLOSE,
      });
    } else {
      closeModal();
    }
  };

  const generateUploadUrlsAndUploadFiles = async (
    filesToUpload: File[],
    filesWithPageCounts: Array<{
      fileName: string;
      fileType: AppealSupportingDocumentFileType;
      pageCount: number;
    }>,
    documentType: AppealSupportingDocumentType,
    otherDocumentTypeName: string | undefined,
  ) => {
    const generateInput = {
      documentType,
      otherDocumentTypeName,
      files: filesWithPageCounts,
    };

    const generateResult = await generateUploadUrls({
      variables: { appealId, document: generateInput },
    });

    const generateData =
      generateResult.data?.generateSupportingDocumentUploadUrls;

    if (
      !generateData ||
      generateData.fileUploadDetails.length !== filesToUpload.length
    ) {
      throw new Error("Failed to generate upload URLs");
    }

    await Promise.all(
      filesToUpload.map((file, index) =>
        uploadFileToUrl(file, generateData.fileUploadDetails[index].uploadUrl),
      ),
    );

    return generateData;
  };

  const buildUpsertInput = (
    documentId: string,
    documentType: AppealSupportingDocumentType,
    otherDocumentTypeName: string | undefined,
    filesWithPageCounts: Array<{
      fileName: string;
      fileType: AppealSupportingDocumentFileType;
      pageCount: number;
    }>,
    fileIds: string[],
  ) => ({
    documentId,
    documentType,
    otherDocumentTypeName,
    files: filesWithPageCounts.map((file, index) => ({
      ...file,
      fileId: fileIds[index],
    })),
  });

  const handleSaveDocument = async () => {
    setSaveDocumentError(null);

    if (!selectedDocumentType) {
      setCurrentView({ view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE });
      return;
    }

    try {
      setIsDocumentUpsertLoading(true);

      const otherDocumentTypeName =
        selectedDocumentType === AppealSupportingDocumentType.Other
          ? otherDocumentName
          : undefined;

      const filesWithPageCounts = await Promise.all(
        uploadedFiles.map(async (file) => {
          const pageCount = await getPageCountForFile(file);
          return {
            fileName: file.name,
            fileType:
              file.type === MIME_TYPES.pdf
                ? AppealSupportingDocumentFileType.Pdf
                : AppealSupportingDocumentFileType.Image,
            pageCount,
          };
        }),
      );

      const generateData = await generateUploadUrlsAndUploadFiles(
        uploadedFiles,
        filesWithPageCounts,
        selectedDocumentType,
        otherDocumentTypeName,
      );

      const fileIds = generateData.fileUploadDetails.map(
        (detail) => detail.fileId,
      );

      const upsertDocumentInput = buildUpsertInput(
        generateData.documentId,
        selectedDocumentType,
        otherDocumentTypeName,
        filesWithPageCounts,
        fileIds,
      );

      if (document?.documentId) {
        await deleteDocument({
          variables: { documentId: document.documentId },
        });
      }

      await upsertAppealSupportingDocument({
        variables: { appealId, document: upsertDocumentInput },
      });

      onSubmitDocument();
      closeModal();
    } catch (error) {
      const clientSafeMessage = getClientSafeErrorMessage(error);
      setSaveDocumentError(clientSafeMessage ?? "Failed to upload, try again");
    } finally {
      setIsDocumentUpsertLoading(false);
    }
  };

  const handleFileView = (file: File) => {
    setCurrentView({
      view: DocumentUploadModalView.VIEW_FILE,
      file,
    });
  };

  const goBackToSelectDocumentType = () => {
    setUploadedFiles([]);
    setSaveDocumentError(null);

    if (mode === DocumentUploadModalMode.ADD) {
      setCurrentView({
        view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
      });
    } else {
      handleOnClose();
    }
  };

  const handleUploadDocumentViewOnBack = () => {
    if (mode === DocumentUploadModalMode.ADD && hasUnsavedChanges) {
      setCurrentView({
        view: DocumentUploadModalView.CLOSE_CONFIRMATION,
        navigationDirection: DocumentUploadModalNavigationDirection.BACK,
      });
    } else if (
      [
        DocumentUploadModalMode.EDIT,
        DocumentUploadModalMode.ADD_LOMN,
        DocumentUploadModalMode.ADD_LAB_RESULTS,
        DocumentUploadModalMode.ADD_DENIAL_LETTER,
      ].includes(mode) &&
      hasUnsavedChanges
    ) {
      setCurrentView({
        view: DocumentUploadModalView.CLOSE_CONFIRMATION,
        navigationDirection: DocumentUploadModalNavigationDirection.CLOSE,
      });
    } else if (mode === DocumentUploadModalMode.ADD) {
      goBackToSelectDocumentType();
    } else {
      closeModal();
    }
  };

  return (
    <Modal
      classNames={{ header: styles.modalHeader }}
      opened={isOpen}
      onClose={handleOnClose}
      title={
        [
          DocumentUploadModalView.DELETE_CONFIRMATION,
          DocumentUploadModalView.CLOSE_CONFIRMATION,
        ].includes(currentView.view) ? undefined : (
          <Title
            currentView={currentView.view}
            onNavigate={() => {
              if (!selectedDocumentType) {
                setCurrentView({
                  view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
                });
              } else {
                setCurrentView({
                  view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                  documentType: selectedDocumentType,
                  otherDocumentName,
                });
              }
            }}
          />
        )
      }
      withCloseButton={
        [
          DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
          DocumentUploadModalView.UPLOAD_DOCUMENT,
        ].includes(currentView.view) && !isDocumentUpsertLoading
      }
      closeOnEscape={
        !isDocumentUpsertLoading &&
        currentView.view !== DocumentUploadModalView.CLOSE_CONFIRMATION
      }
      closeOnClickOutside={
        !isDocumentUpsertLoading &&
        currentView.view !== DocumentUploadModalView.CLOSE_CONFIRMATION
      }
      size={
        currentView.view === DocumentUploadModalView.VIEW_FILE ? "xl" : "md"
      }
    >
      {currentView.view === DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE && (
        <ChooseDocumentTypeModalView
          selectedDocumentType={selectedDocumentType}
          setSelectedDocumentType={setSelectedDocumentType}
          otherDocumentName={otherDocumentName}
          setOtherDocumentName={setOtherDocumentName}
          onContinue={() => {
            if (selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            }
          }}
        />
      )}
      {currentView.view === DocumentUploadModalView.UPLOAD_DOCUMENT &&
        selectedDocumentType && (
          <UploadDocumentModalView
            selectedDocumentType={selectedDocumentType}
            files={uploadedFiles}
            onBack={handleUploadDocumentViewOnBack}
            onFilesUploaded={(files: File[]) => {
              setSaveDocumentError(null);
              setUploadedFiles([...uploadedFiles, ...files]);
            }}
            onViewFile={handleFileView}
            onSaveDocument={handleSaveDocument}
            onDeleteFile={(file: File) => {
              setCurrentView({
                view: DocumentUploadModalView.DELETE_CONFIRMATION,
                file,
              });
            }}
            error={saveDocumentError ?? undefined}
            isSubmitLoading={isDocumentUpsertLoading}
            isLoadingInitialState={isLoadingDocumentFiles}
            mode={mode}
          />
        )}
      {currentView.view === DocumentUploadModalView.VIEW_FILE && (
        <ViewFileModalView
          title={currentView.file.name}
          fileUrls={viewFileUrls ?? []}
          isLoading={isGettingViewFileUrls}
          hasError={hasErrorGettingViewFileUrls}
          size={currentView.file.size}
        />
      )}
      {currentView.view === DocumentUploadModalView.DELETE_CONFIRMATION && (
        <DeleteConfirmationModalView
          name={currentView.file.name}
          onCancel={() => {
            if (selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            } else {
              setCurrentView({
                view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
              });
            }
          }}
          onDelete={() => {
            setSaveDocumentError(null);
            setUploadedFiles(
              uploadedFiles.filter((file) => file !== currentView.file),
            );

            if (selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            } else {
              setCurrentView({
                view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
              });
            }
          }}
        />
      )}
      {currentView.view === DocumentUploadModalView.CLOSE_CONFIRMATION && (
        <CloseConfirmationModalView
          onCancel={() => {
            if (!selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
              });
            } else {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            }
          }}
          onConfirm={
            currentView.navigationDirection ===
            DocumentUploadModalNavigationDirection.BACK
              ? goBackToSelectDocumentType
              : closeModal
          }
          mode={mode}
          navigationDirection={currentView.navigationDirection}
        />
      )}
    </Modal>
  );
};
